import React, { memo, useRef } from 'react'
import styled from 'styled-components'
import useClickAway from 'react-use/lib/useClickAway'
import useKey from 'react-use/lib/useKey'
import Card from '../card'

export type Functionality = {
  title: string
  run: () => void | Promise<void>
  isDisabled?: boolean
  icon?: React.ReactNode
}

export type FunctionalityGroup = {
  header: React.ReactNode
  functionalities: Functionality[]
}

export type ContextMenuArrowPosition = 'right' | 'top' | 'left' | 'bottom'

export type ContextMenuArrow = {
  position: ContextMenuArrowPosition
  x?: React.CSSProperties['left']
  y?: React.CSSProperties['top']
}

const contextMenuArrowSize = 10

const ContextMenuWrapper = styled(Card)`
  position: absolute;
  z-index: 10;
  padding: 10px;
  overflow: visible;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};

  div.relative {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: 7px;

    .context-menu-section {
      display: flex;
      flex-direction: column;
      gap: 3px;
    }

    .context-menu-section + .context-menu-section {
      border-top: 1px solid rgba(0, 0, 0, 0.08);
      margin-top: 2px;
      padding-top: 7px;
    }

    .context-menu-header {
      color: ${({ theme }) => theme.colors.darkGrey};
      font-size: 0.85rem;
      font-weight: bold;
      margin: 0;
      padding: 5px 0 3px 8px;
      white-space: nowrap;
    }

    .context-menu-icon {
      display: inline-flex;
      align-items: center;
      margin-right: 5px;
    }

    ul {
      list-style: none;
      padding: 0;
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: 3px;

      li {
        padding: 0;
        margin: 0;

        a {
          padding: 5px 10px;
          cursor: pointer;
          background-color: transparent;
          border-radius: ${({ theme }) => theme.borderRadius};
          display: flex;
          align-items: center;
          white-space: nowrap;
          color: ${({ theme }) => theme.colors.darkGrey};

          &.is-disabled {
            cursor: not-allowed;
            opacity: 0.5;
          }

          svg,
          svg > * {
            color: ${({ theme }) => theme.colors.darkGrey};
          }

          &:hover,
          &:focus {
            background-color: ${({ theme }) => theme.colors.primary};
            color: #fff;

            svg,
            svg > * {
              color: #fff;
            }

            &.is-disabled {
              background-color: #444;
            }
          }
        }
      }
    }
  }
`

const ContextMenuArrowElement = styled.span`
  position: absolute;
  width: ${contextMenuArrowSize}px;
  height: ${contextMenuArrowSize}px;
  background: #fff;
  pointer-events: none;
  border-left: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-top: 1px solid ${({ theme }) => theme.colors.mediumGrey};
`

export interface ContextMenuProps extends React.HTMLAttributes<HTMLDivElement> {
  functionalities?: Functionality[]
  header?: React.ReactNode
  functionalityGroups?: FunctionalityGroup[]
  xBasis: 'left' | 'right'
  yBasis: 'top' | 'bottom'
  arrow?: ContextMenuArrow
  style?: React.CSSProperties
  onExit?: () => void
}

function getContextMenuArrowStyle(arrow: ContextMenuArrow): React.CSSProperties {
  const halfArrowSize = contextMenuArrowSize / 2
  const centerPosition = '50%'

  switch (arrow.position) {
    case 'top':
      return {
        top: arrow.y ?? -halfArrowSize,
        left: arrow.x ?? centerPosition,
        transform: 'translateX(-50%) rotate(45deg)',
      }
    case 'bottom':
      return {
        bottom: arrow.y === undefined ? -halfArrowSize : undefined,
        top: arrow.y,
        left: arrow.x ?? centerPosition,
        transform: 'translateX(-50%) rotate(45deg)',
      }
    case 'left':
      return {
        left: arrow.x ?? -halfArrowSize,
        top: arrow.y ?? centerPosition,
        transform: 'translateY(-50%) rotate(45deg)',
      }
    case 'right':
      return {
        right: arrow.x === undefined ? -halfArrowSize : undefined,
        left: arrow.x,
        top: arrow.y ?? centerPosition,
        transform: 'translateY(-50%) rotate(45deg)',
      }
  }
}

function ContextMenu({
  functionalities = [],
  header,
  functionalityGroups = [],
  xBasis,
  yBasis,
  arrow,
  style,
  onExit,
  ...props
}: ContextMenuProps) {
  const wrapperReference = useRef<HTMLDivElement>(null)
  useClickAway(wrapperReference, () => (onExit ? onExit() : undefined))
  useKey('Escape', onExit)

  const renderFunctionality = (functionality: Functionality, index: number) => (
    <li key={index}>
      <a
        href='javascript:;'
        onClick={event => {
          event.preventDefault()
          const promise = functionality.run()
          if (promise && onExit) {
            promise.finally(onExit)
          } else if (onExit) {
            onExit()
          }
        }}
        className={functionality.isDisabled ? 'is-disabled' : undefined}
      >
        {functionality.icon ? (
          <span className='context-menu-icon'>{functionality.icon}</span>
        ) : null}
        {functionality.title}
      </a>
    </li>
  )

  return (
    <ContextMenuWrapper
      {...props}
      ref={wrapperReference}
      style={{
        [xBasis]: 0,
        [yBasis]: 0,
        ...style,
      }}
    >
      {arrow ? (
        <ContextMenuArrowElement aria-hidden='true' style={getContextMenuArrowStyle(arrow)} />
      ) : null}
      <div className='relative'>
        {functionalities.length > 0 ? (
          <div className='context-menu-section'>
            {header ? <div className='context-menu-header'>{header}</div> : null}
            <ul>{functionalities.map(renderFunctionality)}</ul>
          </div>
        ) : null}
        {functionalityGroups.map((functionalityGroup, functionalityGroupIndex) => (
          <div className='context-menu-section' key={functionalityGroupIndex}>
            <div className='context-menu-header'>{functionalityGroup.header}</div>
            <ul>{functionalityGroup.functionalities.map(renderFunctionality)}</ul>
          </div>
        ))}
      </div>
    </ContextMenuWrapper>
  )
}
export default memo(ContextMenu)
